import { Trans } from 'react-i18next'
import styled from 'styled-components'

import {
  ArrowRightIcon,
  Button,
  Typography,
  useHapticFeedback,
} from '@blade/ui-kit'
import { useNetworkLinks } from '@root/entities/network'
import { boostersFontCss, BuyPremiumIcon } from '@root/entities/premium/ui'
import { useAppRoutes } from '@root/features/routing'
import { useTranslation } from '@root/shared/localization'
import { Separator as Separator_ } from '@root/shared/ui-kit'

import type { TxHash } from '@root/entities/transaction'
import type { PurchaseBoostersType } from '@root/features/premium/purchase-boosters'

type Props = {
  type: PurchaseBoostersType
  transactionHash: TxHash
  onClose?: () => void
  onEdit?: () => void
}

export const PaymentDrawerSuccess: React.FC<Props> = ({
  type,
  transactionHash,
  onClose,
}) => {
  const { t } = useTranslation()
  const { openTransactionExplorerLink } = useNetworkLinks()

  const haptics = useHapticFeedback()
  const { navigateToPremiumIndex } = useAppRoutes()

  const handleOpenInExplorer = () => {
    haptics.impactOccurred('light')
    openTransactionExplorerLink(transactionHash)
  }

  return (
    <Root>
      <ButtonCheckTx
        fullWidth
        radius="rounded"
        type="button"
        variant="ghost"
        size="m"
        onClick={handleOpenInExplorer}
      >
        {t('premium.pay.check-tx')}
      </ButtonCheckTx>
      <Separator />

      <Note>
        {type === 'boosters' ? (
          <BuyPremiumIcon product="subscription" size={32} sizeIcon={14} />
        ) : (
          <BuyPremiumIcon product="boosters" size={32} />
        )}
        <NoteText>
          <Trans
            i18nKey={
              type === 'boosters'
                ? 'premium.pay.subscription.success'
                : 'premium.pay.boosters.success'
            }
            components={[<NoteAccent key={0} />]}
          />
        </NoteText>
        <Button
          onClick={() => {
            if (type === 'boosters') {
              haptics.impactOccurred('light')
              onClose?.()
              navigateToPremiumIndex()
            }
          }}
          fullWidth
          type="button"
          variant="secondary"
          size="xs"
          iconLeft={<ArrowRightIcon />}
        />
      </Note>

      <ButtonClose
        radius="rounded"
        onClick={onClose}
        fullWidth
        variant="glass"
        size="m"
        color="gray"
      >
        {t('common.close')}
      </ButtonClose>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  padding-top: 16px;
`

const ButtonCheckTx = styled(Button)`
  background: var(--backgroundA-2);
  color: var(--blue-9);

  justify-self: center;

  &:hover {
    background: var(--backgroundA-2);
    color: var(--blue-9);
  }
`

const Separator = styled(Separator_)`
  margin: 24px 0;
`

const Note = styled.div`
  background: var(--appearanceA-3);

  grid-template-columns: auto 1fr auto;
  display: grid;
  justify-content: center;
  align-items: center;

  margin-bottom: 16px;
  border-radius: 12px;
  padding: 10px;
  gap: 12px;
`

const NoteText = styled(Typography).attrs({
  variant: 'body2-regular',
  component: 'p',
})`
  color: var(--text-11);
`

const NoteAccent = styled(Typography).attrs({
  variant: 'body4-semi-bold',
  component: 'span',
})`
  ${boostersFontCss}
`

const ButtonClose = styled(Button)`
  text-transform: uppercase;

  background:
    radial-gradient(
      81.51% 75% at 50% 87.5%,
      rgba(158, 158, 158, 0.35) 0%,
      rgba(158, 158, 158, 0) 100%
    ),
    var(--backgroundA-3);

  &:hover {
    background:
      radial-gradient(
        81.51% 75% at 50% 87.5%,
        rgba(158, 158, 158, 0.35) 0%,
        rgba(158, 158, 158, 0) 100%
      ),
      var(--backgroundA-3);
  }
`
